import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

// Actions
import { actions as userSongActions } from 'modules/usersongs/actions'

// Components
import { Spinner, Error, Layout, Link } from '@tracklib/kit'
import SEO from 'components/base/seo'
import License from 'components/usersong/license'
import UserSongInformation from 'components/usersong/sample'
import UserSongRevenue from 'components/usersong/accounting'
import BeatSharing from 'components/usersong/sharing'
import UserSongHeader from 'components/usersong/header'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useUserSongWithSongs } from 'modules/usersongs/hooks'
import { useFeatureToggle } from 'modules/featuretoggles/hooks'

import styles from './usersong.css'

type UserSongMenuProps = {
  userSongId: number
}

const UserSongMenu = ({ userSongId }: UserSongMenuProps): JSX.Element => {
  const beatSharingFeatureToggle = useFeatureToggle('beatSharing')
  const menuItems = [
    { label: 'Sample overview', path: '/sample/' },
    { label: 'License details', path: '/license/' },
    { label: 'Revenue history', path: '/accounting/' },
    ...(beatSharingFeatureToggle ? [{ label: 'Sharing', path: '/sharing/' }] : []),
  ]

  return (
    <div className={styles.menu}>
      {menuItems.map(item => (
        <Link
          key={item.path}
          to={`/yoursongs/${userSongId}${item.path}`}
          className={styles.menuItem}
          activeClassName={styles.menuItemActive}
        >
          <span>{item.label}</span>
        </Link>
      ))}
      <div className={styles.overflowSpacer} />
    </div>
  )
}

type UserSongPageProps = {
  match: {
    params: {
      userSongId: string
      location: 'sample' | 'license' | 'accounting' | 'sharing'
    }
  }
}

const UserSongPage = ({
  match: {
    params: { userSongId, location: activePath },
  },
}: UserSongPageProps): JSX.Element => {
  const dispatch = useDispatch()
  const userSong = useUserSongWithSongs(+userSongId)

  const fetchSpecificUserSongRequest = useRequest('FETCH_SPECIFIC_USER_SONG', userSongId)

  useEffect(() => {
    if (!userSong && !fetchSpecificUserSongRequest.isLoading && !fetchSpecificUserSongRequest.hasError) {
      dispatch(userSongActions.fetchSpecificUserSong(+userSongId))
    }
  }, [userSong, dispatch, userSongId, fetchSpecificUserSongRequest])

  if (!userSong || fetchSpecificUserSongRequest.isLoading) {
    return (
      <>
        <SEO title={userSong ? `${userSong.song_title}, a song by you | Tracklib` : 'Your song | Tracklib'} />
        {!fetchSpecificUserSongRequest.isDone ? <Spinner minHeight={500} delay={0} /> : <Error />}
      </>
    )
  }

  const renderContent = () => {
    switch (activePath) {
      case 'sample':
        return <UserSongInformation userSong={userSong} />
      case 'license':
        return <License userSong={userSong} />
      case 'accounting':
        return <UserSongRevenue userSongRevenueReportIds={userSong.revenue_reports} userSong={userSong} />
      case 'sharing':
        return <BeatSharing beatId={userSong.beat} />
      default:
        return <div />
    }
  }
  return (
    <>
      <SEO title={`${userSong.song_title}, a song by you | Tracklib`} />
      <UserSongHeader userSong={userSong} />
      <Layout.SideBar
        contentClassName={styles.content}
        sideBarClassName={styles.sidebar}
        sidebarContent={<UserSongMenu userSongId={+userSongId} />}
        side="left"
        sticky={false}
        maxWidth={1200}
      >
        {renderContent()}
      </Layout.SideBar>
    </>
  )
}

export default UserSongPage
